import { CI_WRITE_MANAGE_ROLES, CI_REGISTER_READ_ROLES } from '@/lib/ci/capabilities';
import { CASES_HOST_ROLES } from '@/lib/hr/cases-access';
import { isGrantedEndToEndSettingsRole } from './settings/settings-authority';
import type { SettingsDelegationPreference } from './settings/settings-delegation';
import { CERTIFICATION_VIEW_ROLES } from '@/lib/workforce/certification-access';
import { RECALL_LOG_ROLES } from '@/lib/payroll/recall-log-roles';
import {
  PEOPLE_SETTINGS_READ_ROLES,
  PEOPLE_SETTINGS_TOOL_READ_ROLES,
} from '@/lib/settings/people-settings';
import {
  ONBOARDING_START_ROLES,
  ONBOARDING_READ_ROLES,
  ONBOARDING_DOCUMENT_VERIFY_ROLES,
  LEARNING_OPERATE_ROLES,
  POLICY_LIBRARY_ROLES,
  POLICY_DRAFT_VIEW_ROLES,
} from '@/lib/workforce/people-content-cohorts';
import { isComplianceOnlyPlan } from '@/lib/billing';
import { REVIEWED_IMPORT_INTAKE_ROLES } from '@/lib/integrations/government/reviewed-import/access';
import { NDIS_OVERLAY_ROLES } from '@/lib/compliance/ndis-access';
import {
  OWN_NOTIFICATION_PREFERENCE_PATH,
  SETTINGS_OWN_NOTIFICATION_ROLES,
} from '@/lib/settings/settings-access-roles';
import { WAITLIST_REVIEWED_IMPORT_ACCESS_ROLES } from '@/lib/integrations/government/reviewed-import/operational-roles';
import {
  CLIENT_CARE_WORKSPACE_ALLOWED_ROLES,
  CLIENT_ASSESSMENT_ALLOWED_ROLES,
  FINANCE_ACCESS_ALLOWED_ROLES,
  FINANCE_CLAIMS_ALLOWED_ROLES,
  FINANCE_CONTRIBUTIONS_ALLOWED_ROLES,
  FRONTLINE_HANDOVER_ALLOWED_ROLES,
  FRONTLINE_INCIDENT_REVIEW_ALLOWED_ROLES,
  FRONTLINE_MEDICATION_ALLOWED_ROLES,
  FRONTLINE_OBSERVATION_ALLOWED_ROLES,
  FRONTLINE_SHIFT_EXECUTION_ALLOWED_ROLES,
  FRONTLINE_WOUND_ALLOWED_ROLES,
  PRUDENTIAL_DEPOSIT_ALLOWED_ROLES,
  ROSTER_MANAGE_ALLOWED_ROLES,
  S8_DISCREPANCY_RESOLUTION_ALLOWED_ROLES,
  SAH_DELIVERY_READINESS_OVERRIDE_ALLOWED_ROLES,
  SAH_FINANCIALS_OVERRIDE_ALLOWED_ROLES,
  SAH_READINESS_ALLOWED_ROLES,
  SAH_VISIT_COMPLETE_ALLOWED_ROLES,
  SAH_EVIDENCE_OVERSIGHT_ALLOWED_ROLES,
  SAH_WP1B_CUTOVER_MANAGER_ROLES,
  STAFF_CREATE_ALLOWED_ROLES,
  STAFF_WORKSPACE_ALLOWED_ROLES,
  TENANT_CONFIGURATION_ALLOWED_ROLES,
  WORKFORCE_PAYROLL_ALLOWED_ROLES,
  getAllowedRolesForWorkflowCapability,
} from './capabilities';
import {
  DASHBOARD_ROLES,
  hasDashboardRoleMapping,
  isRoleAllowedForPath as isDashboardRoleAllowedForPath,
} from './dashboard-roles';
import {
  B2G_INTEGRATION_ROUTE_ROLES,
  B2G_VIEW_ROLES,
  QI_LOCAL_ROUTE_ROLES,
  QI_MANUAL_SUBMISSION_ROLES,
} from './integrations/government/b2g-access';
import { HI_PATIENT_IDENTITY_BATCH_ROLES } from './integrations/government/patient-identity-batch/access';
import { PAY_EVENT_FACT_QUEUE_ROLES } from './payroll/pay-event-fact-integrity';
import { isSystemAdmin, normalizeRole } from './rbac';
import { WORKFORCE_CAPABILITY_ROLE_GRANTS } from './workforce/phase1a-capabilities';
import {
  SETTINGS_PAY_ROLE_ROLES,
  SETTINGS_PAYROLL_PROVIDER_ROLES,
  SETTINGS_SERVICES_AUSTRALIA_ROLES,
} from './settings/settings-access-roles';
import type { WorkflowCapabilityId } from '@statura/types';
import { COMPLIANCE_SETTINGS_WRITE_ROLES } from './compliance-settings/roles';
import { BILLING_READ_ROLES, FINANCE_BILLING_ROLES } from './residential-billing/roles';

// Mirrors SIGNATURE_TEMPLATE_SETTINGS_ROLES in
// apps/web/src/app/(dashboard)/settings/layout.tsx — the leaf RoleGuard cohort
// for /settings/signature-templates (matches the signature-template controlled
// RPC allowlist). Without this PATH_ROLE_ACCESS entry the path fell through to
// /settings = [SYSTEM_ADMIN, TENANT_ADMIN], so QUALITY_OFFICER / CLINICAL_LEAD
// were RPC-authorised + leaf-authorised but blocked by isRoleAllowedForPath.
const SIGNATURE_TEMPLATE_SETTINGS_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'QUALITY_OFFICER',
  'CLINICAL_LEAD',
] as const;

/**
 * Coordinator review of progress notes and significant communications.
 *
 * Stated identically in three places, and a test asserts the three sets are equal:
 *   1. here, as PATH_ROLE_ACCESS['/home-care/notes-review']
 *   2. the leaf layout's requireModule allowlist
 *   3. record_clinical_note_review_controlled's role check
 *
 * Without the PATH_ROLE_ACCESS entry the path falls through to
 * PATH_ROLE_ACCESS['/home-care'] = HOME_CARE_BROAD_ALLOWED_ROLES, which includes
 * CARE_WORKER, FINANCE_OFFICER and ALLIED_HEALTH. Clinical narrative must not
 * reach that cohort — the reason this surface is a sibling of
 * /home-care/exceptions rather than part of it (exceptions is gated on
 * sah.statement.issue, which admits FINANCE_OFFICER).
 *
 * Frontline is deliberately excluded: a worker must not review their own note.
 */
export const NOTE_REVIEW_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'COORDINATOR',
  'CASE_MANAGER',
  'CLINICAL_LEAD',
] as const;

export const SERVICE_EXECUTION_RECONCILIATION_ALLOWED_ROLES = rolesForCapability(
  'service.execution.reconcile',
);

export const INTERNAL_APP_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'CEO',
  'OWNER',
  'OPERATIONS_DIRECTOR',
  'CLINICAL_DIRECTOR',
  'FINANCE_DIRECTOR',
  'QUALITY_DIRECTOR',
  'HR_DIRECTOR',
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
  'REGION_FINANCE_OFFICER',
  'COORDINATOR',
  'TEAM_LEADER',
  'REGISTERED_NURSE',
  'CLINICAL_LEAD',
  'ENROLLED_NURSE',
  'ALLIED_HEALTH',
  'CARE_WORKER',
  'FINANCE_OFFICER',
  'PAYROLL_OFFICER',
  'AGENCY_CONTRACTOR',
  'FACILITY_MANAGER',
  'QUALITY_OFFICER',
  'CASE_MANAGER',
  'LIFESTYLE_OFFICER',
] as const;

export const REGION_OPERATION_ROLES = ['REGION_MANAGER'] as const;
export const REGION_COMPLIANCE_ROLES = ['REGION_MANAGER', 'REGION_COMPLIANCE_OFFICER'] as const;
export const REGION_FINANCE_ROLES = ['REGION_MANAGER', 'REGION_FINANCE_OFFICER'] as const;
export const REGION_ALL_ROLES = [
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
  'REGION_FINANCE_OFFICER',
] as const;

// Mirrors apps/web/src/app/(dashboard)/clients/layout.tsx. RLS still gates
// actual client row visibility by tenant + facility.
export const CLIENTS_PARENT_ALLOWED_ROLES = [
  ...CLIENT_CARE_WORKSPACE_ALLOWED_ROLES,
  ...REGION_ALL_ROLES,
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
  'FINANCE_OFFICER',
] as const;

// Canonical web /care-plans access cohort. MUST mirror the care_plans SELECT
// RLS policy can_read_point_of_care_client so the UI cannot admit a role the
// data layer filters out or exclude one that can read. Rostered
// CARE_WORKER/ALLIED_HEALTH/AGENCY_CONTRACTOR reads are per-client via mobile or
// /clients/[id]/clinical, not this list. Shared by care-plans/layout.tsx, the
// care-plan-ack signed-pdf route, and PATH_ROLE_ACCESS['/care-plans'].
export const CARE_PLAN_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'TEAM_LEADER',
  'CASE_MANAGER',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'QUALITY_OFFICER',
] as const;

const PAYROLL_PARENT_ALLOWED_ROLES = [
  ...WORKFORCE_PAYROLL_ALLOWED_ROLES,
  'REGION_MANAGER',
  'REGION_FINANCE_OFFICER',
  'FACILITY_MANAGER',
] as const;

export const HEAD_OFFICE_ALL_ROLES = [
  'CEO',
  'OWNER',
  'OPERATIONS_DIRECTOR',
  'CLINICAL_DIRECTOR',
  'FINANCE_DIRECTOR',
  'QUALITY_DIRECTOR',
  'HR_DIRECTOR',
] as const;

export const PERSONAL_WORKFORCE_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  ...HEAD_OFFICE_ALL_ROLES,
  ...REGION_ALL_ROLES,
  'COORDINATOR',
  'TEAM_LEADER',
  'REGISTERED_NURSE',
  'CLINICAL_LEAD',
  'ENROLLED_NURSE',
  'ALLIED_HEALTH',
  'CARE_WORKER',
  'AGENCY_CONTRACTOR',
  'FACILITY_MANAGER',
  'QUALITY_OFFICER',
  'CASE_MANAGER',
  'LIFESTYLE_OFFICER',
  // (2026-05-29) FINANCE_OFFICER and COMPLIANCE_OFFICER are staff-login
  // provisionable roles (STAFF_LOGIN_PROVISIONABLE_ROLES) that hold their own
  // training assignments, onboarding, profile, etc., but were previously
  // absent here — so they were denied EVERY /me/* self-service route (incl. the
  // /me/courses link surfaced from their own assigned training). Every
  // staff-login access role must reach its own personal self-service surfaces.
  'FINANCE_OFFICER',
  'PAYROLL_OFFICER',
  'COMPLIANCE_OFFICER',
] as const;

export const CLINICAL_ALERT_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'TEAM_LEADER',
  'CASE_MANAGER',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'QUALITY_OFFICER',
] as const;

export const CLINICAL_ESCALATION_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'TEAM_LEADER',
  'CASE_MANAGER',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'CARE_WORKER',
  'ALLIED_HEALTH',
  'QUALITY_OFFICER',
] as const;

export const CLINICAL_MEDICATION_INCIDENT_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'TEAM_LEADER',
  'CASE_MANAGER',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'CLINICAL_LEAD',
  'QUALITY_OFFICER',
] as const;

// R0.4-b-recovery: the coordinator recovery cohort for the stranded-capture
// record-recovery queue. Kept EXACTLY to the two roles the quarantine RLS and the
// reconcile RPC authorise (COORDINATOR, QUALITY_OFFICER) so the page never renders
// an empty/forbidden surface to a broader role — the data would be zero rows and
// every reconcile call would be rejected server-side anyway.
export const STRANDED_CAPTURE_RECOVERY_ROLES = ['COORDINATOR', 'QUALITY_OFFICER'] as const;

export const CLINICAL_THRESHOLD_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
] as const;

export const CLINICAL_OVERDUE_ASSESSMENT_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'TEAM_LEADER',
  'CASE_MANAGER',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'ALLIED_HEALTH',
  'QUALITY_OFFICER',
] as const;

export const CLINICAL_HANDOVER_ALLOWED_ROLES = FRONTLINE_HANDOVER_ALLOWED_ROLES;

export const CLINICAL_PATHWAY_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'TEAM_LEADER',
  'CASE_MANAGER',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'ALLIED_HEALTH',
  'QUALITY_OFFICER',
] as const;

export const CLINICAL_RESTORATIVE_ALLOWED_ROLES = [
  ...CLINICAL_PATHWAY_ALLOWED_ROLES,
  'LIFESTYLE_OFFICER',
] as const;

export const CLINICAL_SUPERVISION_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'TEAM_LEADER',
  'CASE_MANAGER',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
  'QUALITY_OFFICER',
] as const;

export const CLINICAL_SUPERVISION_ASSIGN_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'CLINICAL_LEAD',
] as const;

// CS-1.2 — RN delegation register read cohort. Grant/revoke is further gated to
// the RN cohort (SYSTEM_ADMIN / TENANT_ADMIN / FACILITY_MANAGER / CLINICAL_LEAD /
// REGISTERED_NURSE) at the action + controlled-RPC layer.
export const CLINICAL_DELEGATION_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'TEAM_LEADER',
  'CASE_MANAGER',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'QUALITY_OFFICER',
] as const;

// CS-1.10 — RN caseload view cohort. Matches clinical/caseload/layout.tsx.
export const CLINICAL_CASELOAD_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
  'QUALITY_OFFICER',
] as const;

// CS-1.2 — RN cohort that may grant / revoke delegations. Matches the
// grant_clinical_delegation_controlled / revoke_clinical_delegation_controlled
// RPC allowlists.
export const CLINICAL_DELEGATION_GRANT_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
] as const;

export const CLINICAL_WOUND_ALLOWED_ROLES = FRONTLINE_WOUND_ALLOWED_ROLES;

export const CLINICAL_READ_ALLOWED_ROLES = [
  ...FRONTLINE_OBSERVATION_ALLOWED_ROLES,
  'FACILITY_MANAGER',
  'CASE_MANAGER',
  'LIFESTYLE_OFFICER',
  'QUALITY_OFFICER',
] as const;

const MEDICATION_RECONCILIATION_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'TEAM_LEADER',
  'CASE_MANAGER',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'QUALITY_OFFICER',
] as const;

const SAH_CLASSIFICATION_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'COORDINATOR',
  'CLINICAL_LEAD',
] as const;

export const PRICING_AGREEMENTS_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_FINANCE_OFFICER',
  'FINANCE_OFFICER',
] as const;

/**
 * Read access to /pricing/agreements (Wattle CHSP readiness P1-5, 4 Nov 2026).
 * A coordinator reads service agreements and their consent state for the
 * clients in their programme scope. Intent: the database already admits
 * COORDINATOR to read service_agreements (service_agreements_select_role_scoped,
 * 20267062100000) and to record CHSP agreement content
 * (record_chsp_agreement_details_controlled). The web keeps every write,
 * signing and send action on PRICING_AGREEMENTS_ALLOWED_ROLES; this list only
 * opens the page and its scoped read actions.
 */
export const PRICING_AGREEMENTS_READ_ROLES = [
  ...PRICING_AGREEMENTS_ALLOWED_ROLES,
  'COORDINATOR',
] as const;

export const HOME_CARE_BROAD_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
  'REGION_FINANCE_OFFICER',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'TEAM_LEADER',
  'CASE_MANAGER',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'CARE_WORKER',
  'AGENCY_CONTRACTOR',
  'ALLIED_HEALTH',
  'QUALITY_OFFICER',
  'FINANCE_OFFICER',
] as const;

export const SAH_CLIENT_DETAIL_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'REGION_FINANCE_OFFICER',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'CLINICAL_LEAD',
  'ALLIED_HEALTH',
  'REGISTERED_NURSE',
  'CASE_MANAGER',
  'TEAM_LEADER',
  'FINANCE_OFFICER',
] as const;

/**
 * Client-safe role check mirroring the `/clients/[id]/sah` layout gate
 * (`requireModule(..., SAH_CLIENT_DETAIL_ALLOWED_ROLES)`). Used by the client
 * record hub to gate the embedded SAH management panels with the SAME (narrower)
 * cohort the standalone SAH route enforced — defence-in-depth over the
 * server-side controlled-RPC cohort checks. Returns false for unknown roles.
 */
export function canAccessSahClientDetail(role: string | null | undefined): boolean {
  const normalized = normalizeRole(role);
  if (!normalized) return false;
  return (SAH_CLIENT_DETAIL_ALLOWED_ROLES as readonly string[])
    .map((r) => normalizeRole(r))
    .includes(normalized);
}

/**
 * The actor cohort permitted to record / cease a SAH financial-hardship (Fee
 * Reduction, form SA462) determination — the SUBSET of the SAH client-detail
 * cohort that is ALSO in the `record_sah_hardship_determination_controlled`
 * RPC actor allowlist (SYSTEM_ADMIN / TENANT_ADMIN / FINANCE_OFFICER). The
 * server action guards with this; the panel hides the Record/Cease affordances
 * for everyone else (display-side defence; the RPC + action remain authoritative).
 */
export const SAH_HARDSHIP_MANAGE_ROLES = SAH_CLIENT_DETAIL_ALLOWED_ROLES.filter((role) =>
  ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FINANCE_OFFICER'].includes(role),
);

/** Client-safe check mirroring the hardship server-action role gate. */
export function canManageSahHardship(role: string | null | undefined): boolean {
  const normalized = normalizeRole(role);
  if (!normalized) return false;
  return (SAH_HARDSHIP_MANAGE_ROLES as readonly string[])
    .map((r) => normalizeRole(r))
    .includes(normalized);
}

/**
 * The actor cohort permitted to inline-edit a SAH quarter budget envelope
 * (allocated / carry-in / carry-out) via `update_sah_quarter_budget_controlled`.
 * This deliberately mirrors that RPC's LIVE actor allowlist EXACTLY —
 * `regulated_actor_context(ARRAY['TENANT_ADMIN','FINANCE_OFFICER'])` — which does
 * NOT special-case SYSTEM_ADMIN (unlike the hardship RPC). Including a role the
 * RPC would reject would surface an edit affordance that 500s, so SYSTEM_ADMIN
 * and COORDINATOR are intentionally excluded here. The server action guards with
 * this set; the panel uses `canEditSahBudget` to hide the Edit affordance for
 * everyone else (display-side defence; the RPC + action remain authoritative).
 */
export const SAH_BUDGET_ADMIN_ROLES = SAH_CLIENT_DETAIL_ALLOWED_ROLES.filter((role) =>
  ['TENANT_ADMIN', 'FINANCE_OFFICER'].includes(role),
);

/** Client-safe check mirroring the SAH quarter-budget-edit server-action role gate. */
export function canEditSahBudget(role: string | null | undefined): boolean {
  const normalized = normalizeRole(role);
  if (!normalized) return false;
  return (SAH_BUDGET_ADMIN_ROLES as readonly string[])
    .map((r) => normalizeRole(r))
    .includes(normalized);
}

export const SAH_CONSULT_BUDGET_ROLES = [...CARE_PLAN_ALLOWED_ROLES, 'FINANCE_OFFICER'] as const;
export const SAH_CONSULT_BUDGET_PROMOTE_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
] as const;

/** Client-safe check mirroring the SAH consult budget scratch-draft action/RPC gate. */
export function canManageSahConsultBudget(role: string | null | undefined): boolean {
  const normalized = normalizeRole(role);
  if (!normalized) return false;
  return (SAH_CONSULT_BUDGET_ROLES as readonly string[])
    .map((r) => normalizeRole(r))
    .includes(normalized);
}

/** Client-safe check mirroring the SAH consult budget promotion writer chain. */
export function canPromoteSahConsultBudget(role: string | null | undefined): boolean {
  const normalized = normalizeRole(role);
  if (!normalized) return false;
  return (SAH_CONSULT_BUDGET_PROMOTE_ROLES as readonly string[])
    .map((r) => normalizeRole(r))
    .includes(normalized);
}

export const SAH_SERVICE_AGREEMENT_MANAGE_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'FINANCE_OFFICER',
  'COORDINATOR',
  'CASE_MANAGER',
] as const;

/** Client-safe check mirroring record/sign service-agreement controlled RPCs. */
export function canManageSahServiceAgreement(role: string | null | undefined): boolean {
  const normalized = normalizeRole(role);
  if (!normalized) return false;
  return (SAH_SERVICE_AGREEMENT_MANAGE_ROLES as readonly string[])
    .map((r) => normalizeRole(r))
    .includes(normalized);
}

export const RESIDENTIAL_DAILY_CARE_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'COORDINATOR',
  'TEAM_LEADER',
  'REGISTERED_NURSE',
  'CLINICAL_LEAD',
  'ENROLLED_NURSE',
  'CARE_WORKER',
  'AGENCY_CONTRACTOR',
  'FACILITY_MANAGER',
  'QUALITY_OFFICER',
  'CASE_MANAGER',
  'LIFESTYLE_OFFICER',
] as const;

export const RESIDENTIAL_FINANCE_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'REGION_FINANCE_OFFICER',
  'FACILITY_MANAGER',
  'FINANCE_OFFICER',
] as const;

export const RESIDENTIAL_ADMISSIONS_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'COORDINATOR',
  'FACILITY_MANAGER',
  'REGISTERED_NURSE',
  'CLINICAL_LEAD',
] as const;

/** Admissions reads only; write guards retain RESIDENTIAL_ADMISSIONS_ALLOWED_ROLES. */
export const RESIDENTIAL_ADMISSIONS_READ_ROLES = [
  ...RESIDENTIAL_ADMISSIONS_ALLOWED_ROLES,
  'COMPLIANCE_OFFICER',
] as const;

/** Occupancy includes finance for the home-month leave attestation; Admissions remains unchanged. */
export const RESIDENTIAL_OCCUPANCY_ALLOWED_ROLES = [
  ...RESIDENTIAL_ADMISSIONS_ALLOWED_ROLES,
  'FINANCE_OFFICER',
] as const;

/**
 * Roles complete_residential_transition_controlled admits (its SQL actor list).
 * Occupancy READ access is wider (REGION_MANAGER); write controls use this list
 * so no one is offered a transfer, discharge or death the database refuses.
 */
export const RESIDENTIAL_TRANSITION_WRITE_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'REGISTERED_NURSE',
  'CLINICAL_LEAD',
] as const;

export const RESIDENTIAL_ACCOMMODATION_ALLOWED_ROLES = [
  ...RESIDENTIAL_FINANCE_ALLOWED_ROLES,
  'COORDINATOR',
  'CLINICAL_LEAD',
] as const;

export const RESIDENTIAL_CARE_MINUTES_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
  'COORDINATOR',
  'FACILITY_MANAGER',
  'REGISTERED_NURSE',
  'CLINICAL_LEAD',
  'QUALITY_OFFICER',
] as const;

// Open disclosure under the Aged Care Act / Quality Standards — limit to
// regulated review cohort. /compliance/open-disclosure/[incidentId] is the
// substantive surface and inherits this guard. Lives here, not in the layout,
// because a Next.js layout may only export its default component.
export const OPEN_DISCLOSURE_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
  'COORDINATOR',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
  'FACILITY_MANAGER',
  'QUALITY_OFFICER',
  // Staff-login CO persona must complete OD as part of SIRS closeout.
  'COMPLIANCE_OFFICER',
] as const;

export const REGULATOR_SUBMISSION_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
  'COORDINATOR',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
  'FINANCE_OFFICER',
] as const;

/**
 * Named cross-tenant pack grants are useful only to roles that can complete the
 * controlled read and download. This is mirrored by the two database actor
 * contexts and recipient-picker projections in migrations 909 and 916.
 */
export const COMPLIANCE_PACK_SHARE_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'QUALITY_DIRECTOR',
  'FACILITY_MANAGER',
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
] as const;

/**
 * Workforce compliance CSV import: the manager cohort narrowed to the roles
 * stage_/commit_compliance_import_batch_controlled accept, so no admitted user
 * reaches a wizard whose staging the database refuses (42501).
 */
export const COMPLIANCE_WORKFORCE_IMPORT_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
] as const;

export const WORKER_COMPLIANCE_MANAGER_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'CLINICAL_LEAD',
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
  'HR_OFFICER',
  'TEAM_LEADER',
] as const;

// Audit P2-A: align with the RPC's module08_contractor_manager_roles().
// RPC has COMPLIANCE_OFFICER (not REGION_COMPLIANCE_OFFICER) and does not
// include REGION_MANAGER. Drifted names produce 42501 surprises.
export const CONTRACTOR_MANAGER_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'CASE_MANAGER',
  'COMPLIANCE_OFFICER',
  'FINANCE_OFFICER',
  'QUALITY_OFFICER',
  'CLINICAL_LEAD',
] as const;

/** W1: `[...CONTRACTOR_MANAGER_ROLES, 'AGENCY_CONTRACTOR', 'ALLIED_HEALTH', ...frontline shift roles]`. */
export const CONTRACTOR_PORTAL_ROUTE_ROLES = Array.from(
  new Set<string>([
    ...CONTRACTOR_MANAGER_ROLES,
    'AGENCY_CONTRACTOR',
    'ALLIED_HEALTH',
    ...FRONTLINE_SHIFT_EXECUTION_ALLOWED_ROLES,
  ]),
);

const CONTRACTOR_SUBMIT_ROLES = [
  ...CONTRACTOR_MANAGER_ROLES,
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'CARE_WORKER',
] as const;

export const HR_ONBOARDING_START_ALLOWED_ROLES = ONBOARDING_START_ROLES;

export const HR_ONBOARDING_PACK_APPLY_ALLOWED_ROLES = [
  ...HR_ONBOARDING_START_ALLOWED_ROLES,
] as const;

export const HR_ONBOARDING_MANAGER_ROLES = ONBOARDING_READ_ROLES;

// Exact live personal-details reader cohort; broader step review does not grant PII access.
// The review page, its route guard and the journey "View submitted details" link all
// read this list through canReviewPersonalDetails so they cannot drift apart.
export const PERSONAL_DETAILS_REVIEW_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'FACILITY_MANAGER',
  'COORDINATOR',
] as const;

export function canReviewPersonalDetails(role: string | null | undefined): boolean {
  const normalized = String(role ?? '').toUpperCase();
  return PERSONAL_DETAILS_REVIEW_ROLES.some((allowed) => allowed === normalized);
}

// Step approval RPCs include regional managers. The onboarding approval parity
// migration keeps the actor-bound RPC allow-lists lockstep with this UI cohort.
// Request-changes has its own allow-list (see
// HR_ONBOARDING_REQUEST_CHANGES_ALLOWED_ROLES below).
export const HR_ONBOARDING_APPROVAL_ALLOWED_ROLES = [
  'CASE_MANAGER',
  'CLINICAL_LEAD',
  'QUALITY_OFFICER',
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'TEAM_LEADER',
  'HR_DIRECTOR',
  'HR_OFFICER',
] as const;

// Mirrors the live request_onboarding_step_changes_controlled allow-list: the
// approval cohort, including REGION_MANAGER (region-scoped) and the HR roles
// added by 20390000000000. Every "Return for correction" / "Request changes"
// control uses canRequestOnboardingStepChanges.
export const HR_ONBOARDING_REQUEST_CHANGES_ALLOWED_ROLES = HR_ONBOARDING_APPROVAL_ALLOWED_ROLES;

export function canRequestOnboardingStepChanges(role: string | null | undefined): boolean {
  const normalized = String(role ?? '').toUpperCase();
  return HR_ONBOARDING_REQUEST_CHANGES_ALLOWED_ROLES.some((allowed) => allowed === normalized);
}

// Mirrors verify_onboarding_document_controlled; upload admission is separate.
// TEAM_LEADER can view the onboarding list but cannot verify worker documents.
export const HR_ONBOARDING_DOCUMENT_REVIEW_ALLOWED_ROLES = ONBOARDING_DOCUMENT_VERIFY_ROLES;

// Existing provider upload cohort; phase 2b does not admit HR to DMS capture.
export const HR_ONBOARDING_DOCUMENT_UPLOAD_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'FACILITY_MANAGER',
  'COORDINATOR',
] as const;

/**
 * The cohort admitted to the /hr namespace at all. Owned here and imported by
 * hr/layout.tsx so the parent gate and the path rules under it cannot drift.
 *
 * Every `/hr/**` entry in PATH_ROLE_ACCESS is bounded by this list at request
 * time: the parent layout runs first, so granting a role here that this list
 * omits declares access the layout withholds. `hrNamespaceBounded()` below makes
 * that ceiling explicit rather than relying on someone remembering it.
 */
export const HR_PARENT_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
  'REGION_FINANCE_OFFICER',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'CLINICAL_LEAD',
  'TEAM_LEADER',
  'QUALITY_OFFICER',
  'FINANCE_OFFICER',
] as const;

/** Clip a cohort to what the /hr parent layout will actually admit. */
function hrNamespaceBounded(roles: readonly string[]): readonly string[] {
  const ceiling = HR_PARENT_ALLOWED_ROLES as readonly string[];
  return roles.filter((role) => ceiling.includes(role));
}

export const HR_TRAINING_ASSIGN_ALLOWED_ROLES = LEARNING_OPERATE_ROLES;

/**
 * A clinical supervision performer is not thereby a People work-home reader.
 * Keep this exclusion beside the composed root-route cohort so a future
 * capability union cannot route a Nurse Practitioner to the list refusal.
 */
const PEOPLE_HOME_EXCLUDED_ROLES = ['NURSE_PRACTITIONER'] as const;

const PEOPLE_HOME_ALLOWED_ROLES = Array.from(
  new Set([
    ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.people.list'],
    ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.onboarding.manage'],
    ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.offboarding.manage'],
    ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.performance.manage'],
    ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.supervision.perform'],
    ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.evidence.export'],
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
  ]),
).filter((role) => !PEOPLE_HOME_EXCLUDED_ROLES.includes(role as 'NURSE_PRACTITIONER'));

/**
 * Who can open the Learning workbench read-only. Mirrors
 * `canViewLearningWorkbench()` in lib/workforce/phase3-learning.ts, which is the
 * predicate the Learning pages themselves enforce.
 */
export const PEOPLE_LEARNING_VIEW_ROLES = Array.from(
  new Set([
    ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.people.view'],
    ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.learning.assign'],
  ]),
);

export const HR_POLICY_MANAGER_ROLES = POLICY_LIBRARY_ROLES;
export const HR_POLICY_DRAFT_VIEW_ROLES = POLICY_DRAFT_VIEW_ROLES;

export const WORKFORCE_OBLIGATIONS_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'QUALITY_OFFICER',
] as const;

export const CERT_GATED_ALLOWANCE_EXPOSURE_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'QUALITY_OFFICER',
] as const;

export const CM_TASK_ENTRY_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'TEAM_LEADER',
  'CASE_MANAGER',
  'CARE_WORKER',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'ALLIED_HEALTH',
  'FINANCE_OFFICER',
] as const;

export const CM_TASK_APPROVAL_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'TEAM_LEADER',
  'CASE_MANAGER',
  'FINANCE_OFFICER',
] as const;

/**
 * PATH_ROLE_ACCESS maps route prefixes to allowed roles for the main dashboard
 * app shell. More specific create/reporting paths are listed before their
 * broader module prefixes and longest-prefix matching is used at runtime.
 */
export const PATH_WORKFLOW_CAPABILITY_ACCESS: Partial<Record<string, WorkflowCapabilityId>> = {
  '/settings/modules': 'tenant.configure',
  '/settings/portal': 'tenant.configure',
  '/settings/roster': 'tenant.configure',
  '/settings/assessments': 'tenant.configure',
  '/settings/cert-allowance-registry': 'tenant.configure',
  '/settings/cm-task-types': 'tenant.configure',
  '/settings/document-templates': 'tenant.configure',
  '/settings/funding-authorisations': 'tenant.configure',
  '/settings/onboarding': 'tenant.configure',
  '/settings/onboarding-packs': 'tenant.configure',
  '/settings/payroll/eba': 'tenant.configure',
  '/settings/payroll/provider-responsibilities': 'tenant.configure',
  '/settings/security': 'tenant.configure',
  '/settings/workflows/closeout': 'tenant.configure',
  '/settings/workflows': 'tenant.configure',
  '/onboarding': 'tenant.configure',
  // Intake admission uses its role union; manual creation retains client.create in its action.
  '/clients/new': undefined,
  '/clients': 'client.care.workspace',
  '/care-plans': 'client.care.workspace',
  '/care-plans/bulk-replan': 'client.care.workspace',
  '/care-plans/synthesize': 'client.care.workspace',
  '/care-plans/*/compose': 'client.care.workspace',
  // The /cm-tasks index page is real and reachable but was never mapped, so
  // check:rbac-consistency reported it as an unmapped concrete route. Its own
  // page.tsx already guards with CM_TASK_ENTRY_ALLOWED_ROLES, so this records
  // the enforcement that exists rather than inventing a new cohort.
  '/cm-tasks': 'client.care.workspace',
  '/cm-tasks/new': 'client.care.workspace',
  '/cm-tasks/approve': 'client.care.workspace',
  '/cm-tasks/unknown': 'client.care.workspace',
  '/assessments/library': 'client.care.workspace',
  '/assessments': 'client.care.workspace',
  '/home-care/assessments': 'client.care.workspace',
  '/home-care/budget-explainer': 'client.care.workspace',
  '/home-care/service-delivery': 'service.delivery.manage',
  '/tasks/service-execution-reconciliation': 'service.execution.reconcile',
  '/home-care/external-roster-import': 'service.delivery.manage',
  '/home-care/care-delivery-settings': 'service.delivery.manage',
  '/home-care/classifications': 'service.delivery.manage',
  '/home-care/claims': 'sah.claim.prepare',
  '/home-care/statements': 'sah.statement.issue',
  '/home-care/budgets': 'sah.statement.issue',
  '/home-care/funding-changes': 'client.care.workspace',
  '/home-care/readiness': 'sah.statement.issue',
  '/home-care/at-hm': 'sah.athm.manage',
  '/roster/leave': 'roster.manage',
  '/roster/live': 'roster.manage',
  '/roster/runtime': 'roster.manage',
  '/roster/board': 'roster.manage',
  '/roster/dispatch': 'roster.manage',
  '/roster/grid': 'roster.manage',
  '/roster/optimiser': 'roster.manage',
  '/roster/forecast': 'workforce.payroll.review',
  '/roster/open-shifts': 'roster.manage',
  '/roster/import': 'roster.manage',
  '/roster/periods': 'roster.manage',
  '/roster/shift-swaps': 'roster.manage',
  '/roster/templates': 'roster.manage',
  '/roster': 'roster.manage',
  '/workforce/capabilities': 'roster.manage',
  '/workforce/roster': 'roster.manage',
  '/roster/handovers': 'frontline.handover.access',
  '/clinical/handovers': 'frontline.handover.access',
  '/roster/recalls': 'workforce.payroll.review',
  '/roster/sleepover-disturbances': 'workforce.payroll.review',
  '/roster/timesheets': 'workforce.payroll.review',
  '/roster/today': 'workforce.payroll.review',
  '/roster/confirm-day': 'workforce.payroll.review',
  // ONE DOOR (2026-08-18): the relocated Today payroll section's home.
  '/roster/time-review': 'workforce.payroll.review',
  '/payroll/provider-handoff': 'workforce.payroll.review',
  '/payroll/fixed-pay': 'workforce.payroll.review',
  '/payroll/award-review': 'workforce.payroll.review',
  '/payroll/corrections': 'workforce.payroll.review',
  '/payroll/policy': 'workforce.payroll.review',
  '/payroll': 'workforce.payroll.review',
  '/reports/workforce/recalls': 'workforce.payroll.review',
  '/observations': 'frontline.observation.record',
  '/clinical/alerts': 'frontline.observation.record',
  '/clinical': 'frontline.observation.record',
  '/medications': 'frontline.medication.administer',
  '/wounds': 'frontline.wound.record',
  '/compliance/incidents/new': 'incident.create',
  '/compliance/incidents': 'incident.review',
  '/compliance/complaints/new': 'complaint.create',
  '/compliance/standards': 'quality.gpms.access',
  '/compliance/quality-indicators': 'quality.gpms.access',
  '/compliance/reporting': 'dex.reporting',
  '/compliance/prudential': 'prudential.access',
  '/finance/claims': 'sah.claim.prepare',
  '/analytics': 'analytics.access',
};

function rolesForCapability(capabilityId: WorkflowCapabilityId): readonly string[] {
  return getAllowedRolesForWorkflowCapability(capabilityId);
}

// Read-only assessment-instrument catalogue review (/assessments/library). This
// set MUST stay aligned with the authorised-role list on the backing RPC
// list_tenant_assessment_library_controlled (migration 20268465000000): the DB
// rejects any other role, so widening the route gate would only produce a
// runtime RPC error rather than access. Deliberately excludes frontline
// CARE_WORKER / AGENCY_CONTRACTOR — reviewing the catalogue is a clinical /
// quality function.
export const ASSESSMENT_LIBRARY_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'TEAM_LEADER',
  'CASE_MANAGER',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'ALLIED_HEALTH',
  'QUALITY_OFFICER',
] as const;

/**
 * Worker pay-profile viewers. Exported so the route rule and the leaf layout that
 * enforces it share one list — previously the path entry granted FINANCE_OFFICER and
 * PAYROLL_OFFICER while the inherited /staff layout denied them, so the access the
 * rule declared was silently withheld.
 */
export const WORKER_PAY_PROFILE_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FINANCE_OFFICER',
  'FACILITY_MANAGER',
  'PAYROLL_OFFICER',
] as const;

// G04 R2: exact public RPC actor cohort; professional scope is checked server-side.
export const COMFORT_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGISTERED_NURSE',
  'CLINICAL_LEAD',
  'ENROLLED_NURSE',
  'CARE_WORKER',
  'SUPPORT_WORKER',
  'CASE_MANAGER',
  'COORDINATOR',
  'FACILITY_MANAGER',
] as const;

export const PATH_ROLE_ACCESS: Record<string, readonly string[]> = {
  // Capability is narrowed to platform.superadmin by the server page and action.
  '/support-assistant-budget': ['SYSTEM_ADMIN'],
  '/onboarding': rolesForCapability('tenant.configure'),
  '/tasks/service-execution-reconciliation': SERVICE_EXECUTION_RECONCILIATION_ALLOWED_ROLES,

  // CARE MANAGEMENT
  '/clients/new': [...rolesForCapability('client.create'), ...REVIEWED_IMPORT_INTAKE_ROLES],
  '/clients/batch-validation': HI_PATIENT_IDENTITY_BATCH_ROLES,
  '/clients/*/sah': SAH_CLIENT_DETAIL_ALLOWED_ROLES,
  '/clients/*/clinical/pathways': CLINICAL_READ_ALLOWED_ROLES,
  '/clients/*/clinical/restorative': CLINICAL_READ_ALLOWED_ROLES,
  '/clients/*/clinical/thresholds': CLINICAL_THRESHOLD_ALLOWED_ROLES,
  '/clients/*/clinical/trends': CLINICAL_READ_ALLOWED_ROLES,
  '/clients/*/clinical': CLINICAL_READ_ALLOWED_ROLES,
  '/clients/*/comfort': COMFORT_ALLOWED_ROLES,
  '/clients/*/devices': CLINICAL_THRESHOLD_ALLOWED_ROLES,
  '/clients/*/medications': FRONTLINE_MEDICATION_ALLOWED_ROLES,
  '/clients/*/observations': FRONTLINE_OBSERVATION_ALLOWED_ROLES,
  // Demo widening (2026-05-05): QUALITY_OFFICER + FINANCE_OFFICER need read access to
  // /clients for the conference RBAC walkthrough. RLS still gates client visibility
  // by tenant + facility so widening the route gate doesn't broaden actual data access.
  '/clients': CLIENTS_PARENT_ALLOWED_ROLES,
  '/care-plans': CARE_PLAN_ALLOWED_ROLES,
  '/care-plans/bulk-replan': CARE_PLAN_ALLOWED_ROLES,
  '/care-plans/synthesize': CARE_PLAN_ALLOWED_ROLES,
  '/care-plans/*/compose': CARE_PLAN_ALLOWED_ROLES,
  '/assessments/library': ASSESSMENT_LIBRARY_ALLOWED_ROLES,
  '/assessments': CLIENT_ASSESSMENT_ALLOWED_ROLES,
  '/care-pathways': [...CLIENT_CARE_WORKSPACE_ALLOWED_ROLES, ...REGION_OPERATION_ROLES] as const,
  '/programs': ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR'],
  // Privileged config accounts retain waitlist visibility; operational import
  // cohort must also reach /waitlist so P02 identity-conflict assignees can open
  // the controlled reviewed-import surface (see operational-roles.ts).
  '/waitlist': [...WAITLIST_REVIEWED_IMPORT_ACCESS_ROLES, ...REVIEWED_IMPORT_INTAKE_ROLES],
  '/cm-tasks': CM_TASK_ENTRY_ALLOWED_ROLES,
  '/cm-tasks/new': CM_TASK_ENTRY_ALLOWED_ROLES,
  '/cm-tasks/approve': CM_TASK_APPROVAL_ALLOWED_ROLES,
  '/cm-tasks/unknown': CM_TASK_ENTRY_ALLOWED_ROLES,

  // CLINICAL
  '/observations': FRONTLINE_OBSERVATION_ALLOWED_ROLES,
  '/clinical/alerts': CLINICAL_ALERT_ALLOWED_ROLES,
  '/clinical/escalations': CLINICAL_ESCALATION_ALLOWED_ROLES,
  '/clinical/handovers': CLINICAL_HANDOVER_ALLOWED_ROLES,
  '/clinical/medication-incidents': CLINICAL_MEDICATION_INCIDENT_ALLOWED_ROLES,
  '/clinical/overdue-assessments': CLINICAL_OVERDUE_ASSESSMENT_ALLOWED_ROLES,
  '/clinical/pathways': CLINICAL_PATHWAY_ALLOWED_ROLES,
  '/clinical/restorative': CLINICAL_RESTORATIVE_ALLOWED_ROLES,
  '/clinical/supervision': CLINICAL_SUPERVISION_ALLOWED_ROLES,
  '/clinical/delegations': CLINICAL_DELEGATION_ALLOWED_ROLES,
  '/clinical/caseload': CLINICAL_CASELOAD_ALLOWED_ROLES,
  '/clinical/wounds': CLINICAL_WOUND_ALLOWED_ROLES,
  '/clinical': CLINICAL_READ_ALLOWED_ROLES,
  '/medications/s8-register': S8_DISCREPANCY_RESOLUTION_ALLOWED_ROLES,
  '/medications/reconciliation': MEDICATION_RECONCILIATION_ALLOWED_ROLES,
  '/medications': FRONTLINE_MEDICATION_ALLOWED_ROLES,
  '/wounds': FRONTLINE_WOUND_ALLOWED_ROLES,
  '/advance-care-plans': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
  ],

  // RESIDENTIAL
  '/residential/admissions': [
    ...RESIDENTIAL_ADMISSIONS_ALLOWED_ROLES,
    ...REVIEWED_IMPORT_INTAKE_ROLES,
  ],
  '/residential/evacuation-list': CARE_PLAN_ALLOWED_ROLES,
  '/residential/adl-events': RESIDENTIAL_DAILY_CARE_ALLOWED_ROLES,
  '/residential/care-minutes': RESIDENTIAL_CARE_MINUTES_ALLOWED_ROLES,
  '/residential/care-minutes/an-acc': RESIDENTIAL_CARE_MINUTES_ALLOWED_ROLES,
  '/residential/care-minutes/daily': RESIDENTIAL_CARE_MINUTES_ALLOWED_ROLES,
  '/residential/rounds': RESIDENTIAL_DAILY_CARE_ALLOWED_ROLES,
  '/residential/dap-accrual': RESIDENTIAL_FINANCE_ALLOWED_ROLES,
  '/residential/daily-living': RESIDENTIAL_DAILY_CARE_ALLOWED_ROLES,
  '/residential/activities': RESIDENTIAL_DAILY_CARE_ALLOWED_ROLES,
  '/residential/community': RESIDENTIAL_DAILY_CARE_ALLOWED_ROLES,
  '/residential/meal-management': RESIDENTIAL_DAILY_CARE_ALLOWED_ROLES,
  '/residential/night-rounds': RESIDENTIAL_DAILY_CARE_ALLOWED_ROLES,
  '/residential/occupancy': RESIDENTIAL_OCCUPANCY_ALLOWED_ROLES,
  '/residential/rooms': RESIDENTIAL_ACCOMMODATION_ALLOWED_ROLES,
  '/residential/visitors': RESIDENTIAL_DAILY_CARE_ALLOWED_ROLES,
  '/residential': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'FACILITY_MANAGER',
    'LIFESTYLE_OFFICER',
  ],

  // HOME CARE
  '/home-care/external-roster-import': ['SYSTEM_ADMIN', 'TENANT_ADMIN'] as const,
  '/home-care/service-delivery': [
    ...rolesForCapability('service.delivery.manage'),
    ...SAH_DELIVERY_READINESS_OVERRIDE_ALLOWED_ROLES,
    ...SAH_FINANCIALS_OVERRIDE_ALLOWED_ROLES,
    ...SAH_VISIT_COMPLETE_ALLOWED_ROLES,
    ...SAH_WP1B_CUTOVER_MANAGER_ROLES,
  ] as const,
  '/home-care/care-delivery-settings': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'QUALITY_OFFICER',
  ] as const,
  '/home-care/assessments': CLIENT_ASSESSMENT_ALLOWED_ROLES,
  '/home-care/schedules': rolesForCapability('service.delivery.manage'),
  '/home-care/service-areas': rolesForCapability('service.delivery.manage'),
  '/home-care/budget-explainer': SAH_CLIENT_DETAIL_ALLOWED_ROLES,
  '/home-care/classifications': SAH_CLASSIFICATION_ALLOWED_ROLES,
  '/home-care/claims': rolesForCapability('sah.claim.prepare'),
  '/home-care/statements': rolesForCapability('sah.statement.issue'),
  '/home-care/at-hm': rolesForCapability('sah.athm.manage'),
  '/home-care/budgets': rolesForCapability('sah.statement.issue'),
  '/home-care/funding-changes': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FACILITY_MANAGER',
    'FINANCE_OFFICER',
    'COORDINATOR',
    'CASE_MANAGER',
    'CLINICAL_LEAD',
    'REGION_MANAGER',
    'REGION_FINANCE_OFFICER',
  ],
  '/home-care/readiness': SAH_READINESS_ALLOWED_ROLES,
  '/home-care/exceptions': rolesForCapability('sah.statement.issue'),
  // Must be listed BEFORE the broad '/home-care' fallback is reached. Longest
  // match wins, but the entry has to exist at all — see NOTE_REVIEW_ALLOWED_ROLES.
  '/home-care/notes-review': NOTE_REVIEW_ALLOWED_ROLES,
  '/home-care/reconciliation': rolesForCapability('sah.statement.issue'),
  '/home-care': HOME_CARE_BROAD_ALLOWED_ROLES,

  // WORKFORCE
  // /me/profile gates on PERSONAL_WORKFORCE_ROLES like every other /me/* route
  // (was INTERNAL_APP_ROLES, which omits COMPLIANCE_OFFICER — a staff-login
  // self-service role — and contradicted both the /me/* layout and siblings).
  '/me/profile': PERSONAL_WORKFORCE_ROLES,
  '/me/roster': PERSONAL_WORKFORCE_ROLES,
  '/me/shift-offers': PERSONAL_WORKFORCE_ROLES,
  '/me/shift-swaps': PERSONAL_WORKFORCE_ROLES,
  '/me/timesheets': PERSONAL_WORKFORCE_ROLES,
  // Staff Messages web author/read — same personal workforce cohort as timesheets/leave.
  '/me/messages': PERSONAL_WORKFORCE_ROLES,
  '/me/pay-documents': PERSONAL_WORKFORCE_ROLES,
  '/me/leave': PERSONAL_WORKFORCE_ROLES,
  '/me/training': PERSONAL_WORKFORCE_ROLES,
  '/me/work-readiness': PERSONAL_WORKFORCE_ROLES,
  '/me/courses': PERSONAL_WORKFORCE_ROLES,
  '/me/performance': PERSONAL_WORKFORCE_ROLES,
  '/me/policies': PERSONAL_WORKFORCE_ROLES,
  '/me/onboarding': PERSONAL_WORKFORCE_ROLES,
  '/me/casual-conversion': PERSONAL_WORKFORCE_ROLES,
  '/me/preferences': PERSONAL_WORKFORCE_ROLES,
  '/me/renewals': PERSONAL_WORKFORCE_ROLES,
  '/me/supervision': PERSONAL_WORKFORCE_ROLES,
  '/me/delegations': PERSONAL_WORKFORCE_ROLES,
  '/me/credentials': PERSONAL_WORKFORCE_ROLES,
  '/me/compliance': PERSONAL_WORKFORCE_ROLES,
  '/me/capability': PERSONAL_WORKFORCE_ROLES,
  '/me/hours': PERSONAL_WORKFORCE_ROLES,
  '/me/clinical-pin': PERSONAL_WORKFORCE_ROLES,
  '/workforce': DASHBOARD_ROLES['/dashboards/workforce']!,
  '/roster/proposals': ROSTER_MANAGE_ALLOWED_ROLES,
  // `/availability` permanently redirects to `/me/availability` from middleware.
  //
  // The source entry is kept, but NOT because the redirect needs it: middleware
  // never consults this table, and that redirect carries no
  // `authPolicy: 'before-redirect'`, so the hop is not gated here. The
  // destination is. It stays because surface gates and tests still probe the
  // old path, and because a later change that DID want auth before the redirect
  // would need it. An earlier version of this comment claimed the redirect
  // would be refused without it, which the Grok audit showed was wrong.
  '/availability': PERSONAL_WORKFORCE_ROLES,
  '/me/availability': PERSONAL_WORKFORCE_ROLES,
  // Phase 3 canonical Learning family. SQL still derives the active actor,
  // tenant-local capability date and target scope for every operation.
  '/people/learning': Array.from(
    new Set([
      ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.people.view'],
      ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.learning.assign'],
      ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.learning.verify_external'],
    ]),
  ),
  '/people/learning/courses': PEOPLE_LEARNING_VIEW_ROLES,
  // Creation and saved-course editor only; retain the catalogue/workbench cohort.
  '/people/learning/courses/*': [...PEOPLE_LEARNING_VIEW_ROLES, 'QUALITY_OFFICER'],
  // Read-only metrics. The page enforces the same cohort via
  // canViewLearningWorkbench(); the CSV export inside it stays on the assign cohort.
  '/people/learning/analytics': PEOPLE_LEARNING_VIEW_ROLES,
  '/people/learning/external-evidence': Array.from(
    new Set([
      ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.people.view'],
      ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.learning.assign'],
      ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.learning.verify_external'],
    ]),
  ),
  '/people/learning/competencies': WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.people.view'],
  // Phase 4 canonical Policies & Conduct family. Page and SQL capability
  // checks further separate governance, assignment and restricted case work.
  '/people/policies-conduct': Array.from(
    new Set([
      // Compliance-only officers use the plan-aware library, not the full workbench.
      ...POLICY_DRAFT_VIEW_ROLES.filter((role) => role !== 'COMPLIANCE_OFFICER'),
      ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.people.view'],
      ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.policy.govern'],
      ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.policy.assign'],
    ]),
  ),
  '/people/policies-conduct/policies': POLICY_DRAFT_VIEW_ROLES,
  '/people/policies-conduct/cases': CASES_HOST_ROLES,
  // A task-safe decision surface exposes no conduct narrative. HR Officers
  // reach it only after the controlled RPC confirms target facility scope.
  '/people/conduct-decisions/**': ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'HR_DIRECTOR', 'HR_OFFICER'],
  // The Code bundle root remains available to the policy/workforce workbench
  // cohort. Every case/history/report child is restricted to the conduct
  // manager capability, including direct links and future one-segment leaves.
  '/people/policies-conduct/code/**': WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.conduct.manage'],
  // The pre-canonical URLs still resolve to the same Code pages, so retain
  // the same child restriction for bookmarked legacy paths.
  '/compliance/code-of-conduct/**': WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.conduct.manage'],
  '/people/policies-conduct/code/investigations':
    WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.conduct.manage'],
  '/people/policies-conduct/code/allegations':
    WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.conduct.manage'],
  // Phase 2/5 canonical People, Worker 360, Development and Evidence families.
  // Route access mirrors the capability cohorts, while every reader/write RPC
  // still derives the tenant, active actor, target scope and authorisation date.
  '/people/evidence': WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.evidence.export'],
  '/people/development/supervision': Array.from(
    new Set([
      ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.people.view'],
      ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.supervision.perform'],
    ]),
  ),
  '/people/development/performance/pips': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'TEAM_LEADER',
  ],
  // QUALITY_OFFICER removed: casual conversion is an award/employment decision with
  // payroll consequences and a quality officer has no stake in an individual's
  // employment type. FINANCE_OFFICER retained and mirrored onto /hr/casual-conversion
  // below, because conversion drives classification and pay.
  '/people/development/casual-conversion': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FACILITY_MANAGER',
    // Records dispute steps and outcomes for workers in their facilities
    // (Paul, 5 Oct 2026); the database applies the facility scope.
    'HR_OFFICER',
    'COORDINATOR',
    'FINANCE_OFFICER',
  ],
  '/people/development/performance': Array.from(
    new Set([
      ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.people.view'],
      ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.performance.manage'],
    ]),
  ),
  '/people/development': Array.from(
    new Set([
      ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.people.view'],
      ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.performance.manage'],
      ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.supervision.perform'],
    ]),
  ),
  '/people/onboarding/new': HR_ONBOARDING_START_ALLOWED_ROLES,
  '/people/onboarding/documents': HR_ONBOARDING_DOCUMENT_REVIEW_ALLOWED_ROLES,
  '/people/onboarding': HR_ONBOARDING_MANAGER_ROLES,
  '/people/onboarding/*/personal-details': PERSONAL_DETAILS_REVIEW_ROLES,
  '/people/offboarding': [
    ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.offboarding.manage'],
    'PAYROLL_OFFICER',
  ],
  '/people/compliance/obligations': WORKFORCE_OBLIGATIONS_ALLOWED_ROLES,
  '/people/compliance/screening': WORKER_COMPLIANCE_MANAGER_ROLES,
  // Self-only certification leaf; no parent People or other compliance admission.
  '/people/compliance/certifications': CERTIFICATION_VIEW_ROLES,
  '/people/compliance': WORKER_COMPLIANCE_MANAGER_ROLES,
  '/people/workers/*/pay-profile': WORKER_PAY_PROFILE_ALLOWED_ROLES,
  '/people/workers': Array.from(
    new Set([
      ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.people.view'],
      'PAYROLL_OFFICER',
      'FINANCE_OFFICER',
    ]),
  ),
  '/people/workers/*': Array.from(
    new Set([
      ...WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.people.view'],
      'PAYROLL_OFFICER',
      'FINANCE_OFFICER',
    ]),
  ),
  '/settings/people': PEOPLE_SETTINGS_READ_ROLES,
  '/settings/people/assignment-requirements': PEOPLE_SETTINGS_TOOL_READ_ROLES,
  '/people': PEOPLE_HOME_ALLOWED_ROLES,
  // The namespace rule and the layout gate are the same cohort by definition —
  // kept as one constant so they cannot drift under maintenance.
  '/hr': HR_PARENT_ALLOWED_ROLES,
  '/hr/casual-conversion': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'FACILITY_MANAGER',
    'COORDINATOR',
    // Mirrors /people/development/casual-conversion so the same screen is not
    // governed by two different rules depending on the URL.
    'FINANCE_OFFICER',
  ] as const,
  '/hr/workforce-obligations': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'QUALITY_OFFICER',
  ] as const,
  '/hr/performance': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'TEAM_LEADER',
  ] as const,
  '/hr/performance/pips': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'FACILITY_MANAGER',
  ] as const,
  // Legacy /hr twins of the canonical People Learning screens. Catalogue and
  // metrics VIEW is deliberately wider than course ASSIGNMENT: writes stay guarded
  // by HR_TRAINING_ASSIGN_ALLOWED_ROLES inside lib/training/training-actions.ts and
  // the CSV export by hr/training/acqsc-report/route.ts, so a wider view here does
  // not widen what anyone can do.
  //
  // Bounded by the /hr parent layout rather than copying the People cohort
  // verbatim. CEO, OWNER, OPERATIONS_DIRECTOR and HR_DIRECTOR are outside
  // HR_PARENT_ALLOWED_ROLES, so granting them here would declare access
  // hr/layout.tsx withholds — they reach these screens at the canonical
  // /people/learning/* routes, which carry no such parent gate.
  '/hr/training/courses': hrNamespaceBounded(PEOPLE_LEARNING_VIEW_ROLES),
  '/hr/training/dashboard': hrNamespaceBounded(PEOPLE_LEARNING_VIEW_ROLES),
  '/hr/training': HR_TRAINING_ASSIGN_ALLOWED_ROLES,
  '/hr/documents': HR_ONBOARDING_DOCUMENT_REVIEW_ALLOWED_ROLES,
  '/hr/onboarding/new': HR_ONBOARDING_START_ALLOWED_ROLES,
  // Onboarding step configuration — tenant-admin only (the page + the controlled RPC both re-gate
  // to SYSTEM_ADMIN/TENANT_ADMIN); listed above the broader /hr/onboarding entry so a coordinator
  // doesn't get a dead link to it.
  '/hr/onboarding/settings': ['SYSTEM_ADMIN', 'TENANT_ADMIN'] as const,
  '/settings/onboarding/worker/base': PEOPLE_SETTINGS_TOOL_READ_ROLES,
  '/hr/onboarding/documents': HR_ONBOARDING_DOCUMENT_REVIEW_ALLOWED_ROLES,
  '/hr/onboarding': hrNamespaceBounded(HR_ONBOARDING_MANAGER_ROLES),
  '/hr/onboarding/bulk-import': WORKER_COMPLIANCE_MANAGER_ROLES,
  // Legacy alias; the canonical provider register is /compliance/workforce.
  '/hr/compliance': WORKER_COMPLIANCE_MANAGER_ROLES,
  '/hr/offboarding': WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.offboarding.manage'],
  '/hr/clinical-supervision': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'CLINICAL_LEAD',
    'TEAM_LEADER',
  ] as const,
  '/hr/policies': hrNamespaceBounded(HR_POLICY_MANAGER_ROLES),
  '/payroll': PAYROLL_PARENT_ALLOWED_ROLES,
  '/payroll/setup': SETTINGS_PAYROLL_PROVIDER_ROLES,
  // Tenant payroll policy propose/approve cohort plus the MZ-004 pay-record
  // writers (PAYROLL_POLICY_PAGE_ROLES; matches the leaf layout).
  '/payroll/policy': [
    'OWNER',
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FINANCE_OFFICER',
    'FACILITY_MANAGER',
    'PAYROLL_OFFICER',
  ] as const,
  '/payroll/queue': PAYROLL_PARENT_ALLOWED_ROLES,
  '/payroll/pay-runs': rolesForCapability('workforce.payroll.review'),
  '/payroll/provider-handoff': rolesForCapability('workforce.payroll.review'),
  // Fixed-pay ledger cohort (matches the page's own ALLOWED_ROLES). Explicit:
  // the wider review capability let a Facility Manager see the Fixed pay tab in
  // the Pay period strip and then meet PermissionDenied (Grok audit, 8 Sep 2026).
  '/payroll/fixed-pay': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FINANCE_OFFICER',
    'PAYROLL_OFFICER',
  ] as const,
  // Award-review sign-off cohort (matches mark_staff_award_reviewed_controlled).
  // Explicit list so FACILITY_MANAGER is included in this narrower cohort.
  '/payroll/award-review': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FINANCE_OFFICER',
    'PAYROLL_OFFICER',
    'FACILITY_MANAGER',
  ] as const,
  // Correction workbench cohort (matches the page's own ALLOWED_ROLES and the
  // propose/review RPC allowlists). Explicit so a coordinator or team leader,
  // who may review timesheets, is not shown a Corrections tab that refuses them.
  '/payroll/corrections': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FINANCE_OFFICER',
    'PAYROLL_OFFICER',
    'FACILITY_MANAGER',
  ] as const,
  '/payroll/readiness': rolesForCapability('workforce.payroll.review'),
  '/payroll/manual-closures': rolesForCapability('workforce.payroll.review'),
  '/payroll/timesheet': rolesForCapability('workforce.payroll.review'),
  '/payroll/reconciliation': rolesForCapability('workforce.payroll.review'),
  // Agency AP-invoice workflow is part of payroll review. Controlled RPCs
  // still enforce tenant scope; this route should not be finance-only because
  // facility operators need to reconcile agency coverage against rosters.
  '/payroll/agency-invoices': rolesForCapability('workforce.payroll.review'),
  '/regions': ['SYSTEM_ADMIN', 'TENANT_ADMIN', ...REGION_ALL_ROLES] as const,
  '/head-office/board-pack': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'CEO',
    'OWNER',
    'OPERATIONS_DIRECTOR',
    'QUALITY_DIRECTOR',
    'FINANCE_DIRECTOR',
    'CLINICAL_DIRECTOR',
  ] as const,
  '/head-office': ['SYSTEM_ADMIN', 'TENANT_ADMIN', ...HEAD_OFFICE_ALL_ROLES] as const,
  '/reports/subscriptions': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...HEAD_OFFICE_ALL_ROLES,
    ...REGION_ALL_ROLES,
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
  ] as const,
  '/reports/cert_gated_allowance_exposure': CERT_GATED_ALLOWANCE_EXPOSURE_ROLES,
  '/reports/workforce/recalls': rolesForCapability('workforce.payroll.review'),
  '/reports/sah': HOME_CARE_BROAD_ALLOWED_ROLES,
  '/reports': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...HEAD_OFFICE_ALL_ROLES,
    ...REGION_ALL_ROLES,
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'FINANCE_OFFICER',
    'PAYROLL_OFFICER',
  ] as const,
  '/activity': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'COORDINATOR',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
  ],
  '/staff/new': STAFF_CREATE_ALLOWED_ROLES,
  '/staff': STAFF_WORKSPACE_ALLOWED_ROLES,
  '/contractors/evidence/queue': CONTRACTOR_MANAGER_ROLES,
  '/contractors/reconciliation': CONTRACTOR_MANAGER_ROLES,
  '/contractors/new': CONTRACTOR_MANAGER_ROLES,
  '/contractors/*/engagements/new': CONTRACTOR_MANAGER_ROLES,
  // Service-delivery + invoice routes require manager-level authority
  // because the RPCs underlying them refuse non-manager roles.
  '/contractors/*/services/new': CONTRACTOR_MANAGER_ROLES,
  '/contractors/*/invoices/new': CONTRACTOR_MANAGER_ROLES,
  '/contractors/services': CONTRACTOR_MANAGER_ROLES,
  '/contractors/invoices': CONTRACTOR_MANAGER_ROLES,
  '/contractors': CONTRACTOR_SUBMIT_ROLES,
  // Contractor management W1 (20 Oct 2026, docs/source-of-truth/contractor-management.md §9):
  // record workspace tabs narrow to the contractor manager cohort (the RPCs refuse others).
  '/contractors/*/people': CONTRACTOR_MANAGER_ROLES,
  '/contractors/*/compliance': CONTRACTOR_MANAGER_ROLES,
  '/contractors/*/bank-account': CONTRACTOR_MANAGER_ROLES,
  // Contractor self-serve portal. Open to contractors who hold an ordinary
  // care role; the page guard then resolves the contractor link
  // (resolveContractorIdentity), so an unlinked care worker sees the unlinked
  // state rather than a 403, and data stays RLS-scoped to the linked contractor.
  '/me/contractor/log': CONTRACTOR_PORTAL_ROUTE_ROLES,
  '/me/contractor/invoice': CONTRACTOR_PORTAL_ROUTE_ROLES,
  '/me/contractor': CONTRACTOR_PORTAL_ROUTE_ROLES,
  '/me/contractor/compliance': CONTRACTOR_PORTAL_ROUTE_ROLES,
  '/me/contractor/shifts': CONTRACTOR_PORTAL_ROUTE_ROLES,
  '/me/contractor/invoices': CONTRACTOR_PORTAL_ROUTE_ROLES,
  '/me/contractor/profile': CONTRACTOR_PORTAL_ROUTE_ROLES,
  '/me/contractor/bank-account': CONTRACTOR_PORTAL_ROUTE_ROLES,
  '/workforce/roster': [...ROSTER_MANAGE_ALLOWED_ROLES],
  '/workforce/capabilities': ROSTER_MANAGE_ALLOWED_ROLES,
  '/workforce/availability': ROSTER_MANAGE_ALLOWED_ROLES,
  '/workforce/handovers': rolesForCapability('frontline.handover.access'),
  '/workforce/mobile-activity': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'COORDINATOR',
    'TEAM_LEADER',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
  ],
  '/workforce/timesheets/disputes': rolesForCapability('workforce.payroll.review'),
  '/roster/handovers': rolesForCapability('frontline.handover.access'),
  '/roster/recalls': rolesForCapability('workforce.payroll.review'),
  // Logging a recall is open to frontline staff as well as payroll reviewers.
  '/roster/recalls/new': RECALL_LOG_ROLES,
  '/roster/sleepover-disturbances': rolesForCapability('workforce.payroll.review'),
  '/roster/timesheets': rolesForCapability('workforce.payroll.review'),
  '/roster/timesheets/reconciliation': rolesForCapability('workforce.payroll.review'),
  '/roster/today': rolesForCapability('workforce.payroll.review'),
  '/roster/confirm-day': rolesForCapability('workforce.payroll.review'),
  '/roster/health': ROSTER_MANAGE_ALLOWED_ROLES,
  '/roster/leave': ROSTER_MANAGE_ALLOWED_ROLES,
  '/roster/wing-staffing': ROSTER_MANAGE_ALLOWED_ROLES,
  '/roster/board': ROSTER_MANAGE_ALLOWED_ROLES,
  '/roster/dispatch': ROSTER_MANAGE_ALLOWED_ROLES,
  '/roster/grid': ROSTER_MANAGE_ALLOWED_ROLES,
  '/roster/optimise-day': ROSTER_MANAGE_ALLOWED_ROLES,
  '/roster/open-shifts': ROSTER_MANAGE_ALLOWED_ROLES,
  '/roster/import': ROSTER_MANAGE_ALLOWED_ROLES,
  '/roster/periods': ROSTER_MANAGE_ALLOWED_ROLES,
  '/roster/templates': ROSTER_MANAGE_ALLOWED_ROLES,
  '/audit/evidence': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'QUALITY_OFFICER',
    'FINANCE_OFFICER',
    'CLINICAL_LEAD',
  ] as const,
  '/roster/runs': [...ROSTER_MANAGE_ALLOWED_ROLES] as const,
  '/roster/unfunded-visits': [...ROSTER_MANAGE_ALLOWED_ROLES] as const,
  '/roster/continuity': [...ROSTER_MANAGE_ALLOWED_ROLES] as const,
  /*
     ONE DOOR (2026-08-18): the runtime/live aliases now 307 to the dispatch
     day board (roster.manage). The former REGISTERED_NURSE / CLINICAL_LEAD
     VIEW cohort of the standalone live board is deliberately narrowed with
     the fold — flagged in the fold report for owner sign-off; restoring it
     requires a decision about clinical read access to the manager board, not
     a silent role widening here.
  */
  '/roster/runtime': ROSTER_MANAGE_ALLOWED_ROLES,
  '/roster/live': ROSTER_MANAGE_ALLOWED_ROLES,
  '/roster/optimiser': [...ROSTER_MANAGE_ALLOWED_ROLES, 'FACILITY_MANAGER'] as const,
  '/roster/forecast': [...ROSTER_MANAGE_ALLOWED_ROLES, 'FINANCE_OFFICER'] as const,
  '/roster/shift-swaps': ROSTER_MANAGE_ALLOWED_ROLES,
  '/roster/replace': [
    ...ROSTER_MANAGE_ALLOWED_ROLES,
    'CARE_WORKER',
    'REGISTERED_NURSE',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'AGENCY_CONTRACTOR',
    'CASE_MANAGER',
    'LIFESTYLE_OFFICER',
    'CLINICAL_LEAD',
  ] as const,
  /*
     ONE DOOR (2026-08-18): /roster is a middleware redirect to the dispatch
     board, so it carries the board's cohort. The other merged cohorts keep
     their own doors — finance/payroll bookmarks (?section=time-review /
     confirm-day) are REMAPPED by middleware to /roster/time-review before any
     route gate runs, and SAH case managers land on /roster/visit-exceptions —
     so narrowing this entry strands no section bookmark.
  */
  '/roster': ROSTER_MANAGE_ALLOWED_ROLES,
  '/roster/time-review': rolesForCapability('workforce.payroll.review'),
  // Exactly the cohort the page guard and the resolve action enforce
  // (TODAY_VISIT_EXCEPTION_ROLES) — no wider, so nav never offers a door the
  // page then refuses.
  '/roster/visit-exceptions': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'TEAM_LEADER',
    'CASE_MANAGER',
  ] as const,
  '/workflows/instances': [
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'COORDINATOR',
    'CASE_MANAGER',
    'FACILITY_MANAGER',
  ],
  // Open disclosure — SIRS closeout step (RoleGate fail-closed without this)
  '/compliance/open-disclosure': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'COORDINATOR',
    'CLINICAL_LEAD',
    'REGISTERED_NURSE',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
  ] as const,
  '/compliance/training-delivery': WORKER_COMPLIANCE_MANAGER_ROLES,
  '/compliance/validity-rules': WORKER_COMPLIANCE_MANAGER_ROLES,
  '/compliance/capability-framework': WORKER_COMPLIANCE_MANAGER_ROLES,
  '/compliance/audit-pack': REGULATOR_SUBMISSION_ALLOWED_ROLES,
  // Wave 13 is registered here while the W10 Standards/audit nav group is unmerged.
  '/compliance/evidence-packs': REGULATOR_SUBMISSION_ALLOWED_ROLES,
  '/compliance/screening': WORKER_COMPLIANCE_MANAGER_ROLES,
  '/compliance/certifications': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'COORDINATOR',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
    'HR_OFFICER',
    'TEAM_LEADER',
    'CARE_WORKER',
    'REGISTERED_NURSE',
    'ENROLLED_NURSE',
    'PERSONAL_CARE_WORKER',
    'ASSISTANT_IN_NURSING',
    'ALLIED_HEALTH',
    'ALLIED_HEALTH_PROFESSIONAL',
    'SOCIAL_SUPPORT_WORKER',
    'DOMESTIC_WORKER',
    'DOMESTIC_ASSISTANCE_WORKER',
    'TRANSPORT_DRIVER',
  ],
  '/compliance/workforce': WORKER_COMPLIANCE_MANAGER_ROLES,
  '/compliance/workforce/import': COMPLIANCE_WORKFORCE_IMPORT_ROLES,
  '/compliance/provider-obligations': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'COORDINATOR',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
    'CASE_MANAGER',
  ] as const,
  '/compliance/ndis-overlay': NDIS_OVERLAY_ROLES,
  '/compliance/sah-evidence': SAH_EVIDENCE_OVERSIGHT_ALLOWED_ROLES,
  '/compliance/services-australia': ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR'] as const,
  '/integrations/services-australia': ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR'] as const,

  // COMPLIANCE
  '/compliance/star-ratings/methodology': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
  ],
  '/compliance/incidents/new': rolesForCapability('incident.create'),
  '/compliance/incidents': FRONTLINE_INCIDENT_REVIEW_ALLOWED_ROLES,
  // Wave 8 slice 3 "My compliance work" worklist. Read access mirrors the
  // broad '/compliance' cohort (this is a personal work surface, not a
  // register write path) — assignment WRITES are separately gated by
  // COMPLIANCE_ASSIGNMENT_MUTATION_ROLE_LIST inside the reused Wave 8 slice 2
  // actions and this route's own owner-search action, which is the narrower
  // enforcing boundary for mutations.
  '/compliance/work': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'QUALITY_DIRECTOR',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
    'CASE_MANAGER',
    'TEAM_LEADER',
  ],
  '/compliance/calendar': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'QUALITY_DIRECTOR',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
    'CASE_MANAGER',
    'TEAM_LEADER',
  ],
  '/compliance/complaints/new': rolesForCapability('complaint.create'),
  '/compliance/complaints': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FACILITY_MANAGER',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'CLINICAL_LEAD',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
    'REGISTERED_NURSE',
  ],
  // Executive and director roles plus TEAM_LEADER already reached this screen
  // through /people/policies-conduct/governance. Aligning here rather than removing
  // it there: policy governance visibility for a CEO / Operations Director is
  // expected, and the narrower list was the stale side of the split.
  '/compliance/policies': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
    'CEO',
    'OWNER',
    'OPERATIONS_DIRECTOR',
    'HR_DIRECTOR',
    'TEAM_LEADER',
  ],
  '/compliance/standards': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'CARE_WORKER',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
    'CASE_MANAGER',
  ],
  '/compliance/quality/feedback': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'CARE_WORKER',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
    'CASE_MANAGER',
  ],
  '/compliance/quality-indicators': QI_LOCAL_ROUTE_ROLES,
  '/compliance/quality/qi-data-entry/submit': QI_MANUAL_SUBMISSION_ROLES,
  '/compliance/quality/qi-data-entry/reporting': B2G_VIEW_ROLES,
  '/compliance/b2g/audit': B2G_VIEW_ROLES,
  '/compliance/quality': rolesForCapability('quality.gpms.access'),
  '/compliance/clinical-governance': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
  ],
  '/compliance/rn-coverage': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
  ],
  '/compliance/environmental': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
  ],
  '/compliance/data-breach': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
  ],
  '/compliance/star-ratings': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'CLINICAL_LEAD',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
  ],
  '/compliance/restrictive-practices': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'CARE_WORKER',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
    'CASE_MANAGER',
  ],
  '/compliance/code-of-conduct': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'CARE_WORKER',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
    'CASE_MANAGER',
  ],
  '/compliance/whistleblower': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'CARE_WORKER',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
    'CASE_MANAGER',
  ],
  '/compliance/continuous-improvement/new': [...CI_WRITE_MANAGE_ROLES],
  '/compliance/continuous-improvement': [...CI_REGISTER_READ_ROLES],
  '/compliance/audit-program/new': rolesForCapability('quality.gpms.access'),
  '/compliance/audit-program/*': rolesForCapability('quality.gpms.access'),
  '/compliance/audit-program': rolesForCapability('quality.gpms.access'),
  '/compliance/audit-trail': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'CLINICAL_LEAD',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
  ],
  '/compliance/audit': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'CLINICAL_LEAD',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
  ],
  '/compliance/documents': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'CARE_WORKER',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
    'CASE_MANAGER',
    'TEAM_LEADER',
  ],
  '/compliance/documents/expiring': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'CARE_WORKER',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
    'CASE_MANAGER',
  ],
  '/compliance/reporting': rolesForCapability('dex.reporting'),
  // R0.4-b-recovery: exact entry so the shell narrows to the recovery cohort
  // rather than falling back to the broad '/compliance' cohort (which would route
  // broader roles to a dead restricted page). Mirrors the reconcile RPC's gate.
  '/compliance/record-recovery': STRANDED_CAPTURE_RECOVERY_ROLES,
  '/compliance': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'CARE_WORKER',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
    'CASE_MANAGER',
  ] as const,

  // GOVERNANCE
  '/compliance/governance': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
  ],
  '/compliance/governance/qcab': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
  ],
  '/compliance/responsible-persons': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
  ],
  '/compliance/registration': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
  ],
  '/compliance/prudential/accommodation-agreements': PRUDENTIAL_DEPOSIT_ALLOWED_ROLES,
  '/compliance/prudential/deposits': PRUDENTIAL_DEPOSIT_ALLOWED_ROLES,
  '/compliance/prudential': rolesForCapability('prudential.access'),

  // HEALTH & SAFETY
  '/compliance/whs/incidents': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'CARE_WORKER',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'TEAM_LEADER',
    'COMPLIANCE_OFFICER',
    'CASE_MANAGER',
  ],
  '/compliance/whs': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'CARE_WORKER',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'TEAM_LEADER',
    'COMPLIANCE_OFFICER',
  ],
  '/compliance/ipc': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_COMPLIANCE_ROLES,
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'CARE_WORKER',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
  ],

  // FINANCE
  '/finance/claims': FINANCE_CLAIMS_ALLOWED_ROLES,
  '/finance/contributions': FINANCE_CONTRIBUTIONS_ALLOWED_ROLES,
  '/finance/rate-drift': ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'OWNER', 'FINANCE_OFFICER'] as const,
  // Route access includes readers; mutation guards remain finance-only.
  '/finance/residential-billing': BILLING_READ_ROLES,
  '/finance/accommodation': FINANCE_BILLING_ROLES,
  // Explicit (was inheriting the /finance prefix) — AN-ACC funding view is a RAC finance read surface.
  '/finance/an-acc': FINANCE_ACCESS_ALLOWED_ROLES,
  '/finance/respite-billing': [
    'COMPLIANCE_OFFICER',
    'QUALITY_OFFICER',
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'CASE_MANAGER',
    'FINANCE_OFFICER',
    'CLINICAL_LEAD',
    'REGISTERED_NURSE',
  ],
  // Overnight provider-managed billing build (2026-08-18). Each entry mirrors
  // the actor list already enforced inside the route's own server actions —
  // see the file cited on each line — so the app gate and the in-action
  // requireModule() gate cannot silently diverge.
  // Mirrors BILLING_RUN_ROLES in finance/billing/actions.ts (itself a copy of
  // billing-review's BILLING_REVIEW_ROLES).
  '/finance/billing': [
    'OWNER',
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'CASE_MANAGER',
    'FINANCE_OFFICER',
    'REGION_FINANCE_OFFICER',
  ],
  // Cursor-audit finding 5: widened to match BILLING_RUN_ROLES above —
  // billing_period_readiness_controlled's actor array
  // (20271030100011_billing_period_readiness_rpc.sql) and READINESS_ROLES
  // (lib/billing/readiness-actions.ts) were widened in the same change so a
  // role admitted to the billing-run shell is never turned away by Stage 1's
  // own readiness call.
  '/finance/billing/readiness': [
    'OWNER',
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'CASE_MANAGER',
    'FINANCE_OFFICER',
    'REGION_FINANCE_OFFICER',
  ],
  // Mirrors BILLING_PAYER_ROLES in lib/finance/payer-actions.ts and the
  // regulated_actor_context array in 20271030100015_billing_payer_entity.sql.
  // deep-audit cursor finding 5: OWNER added — see BILLING_PAYER_ROLES.
  '/finance/payers': [
    'OWNER',
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'CASE_MANAGER',
    'FINANCE_OFFICER',
    'REGION_MANAGER',
    'REGION_FINANCE_OFFICER',
  ],
  // Mirrors CHARGE_CAPTURE_ROLES in finance/charges/actions.ts (the wider of
  // its two cohorts — capture vs. attach — since attach is the narrower
  // subset, not a separate route).
  '/finance/charges': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'OWNER',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'CASE_MANAGER',
    'FINANCE_OFFICER',
    'REGION_MANAGER',
    'REGION_FINANCE_OFFICER',
  ],
  // The statements page itself has no in-route role gate (RLS is the only
  // defence today — see this package's NOTES.md); this entry mirrors
  // get_payer_statement_controlled's actor array
  // (20271030100024_payer_statement_read_model.sql) so the app-level route
  // gate is at least as narrow as the data the page can actually show.
  '/finance/statements': [
    'OWNER',
    'TENANT_ADMIN',
    'FINANCE_OFFICER',
    'SYSTEM_ADMIN',
    'COORDINATOR',
  ],
  '/finance': FINANCE_ACCESS_ALLOWED_ROLES,
  '/pricing': FINANCE_ACCESS_ALLOWED_ROLES,
  '/pricing/agreements': PRICING_AGREEMENTS_READ_ROLES,
  '/pricing/rate-cards': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_FINANCE_OFFICER',
    'FINANCE_OFFICER',
    'COORDINATOR',
    'CASE_MANAGER',
  ],

  // MONITORING
  '/monitoring': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
  ],

  // TELEHEALTH
  '/telehealth': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ALLIED_HEALTH',
    'ENROLLED_NURSE',
  ],

  // ANALYTICS
  '/analytics/board-pack': ['SYSTEM_ADMIN', 'TENANT_ADMIN'],
  '/analytics/benchmarking': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'QUALITY_OFFICER',
  ],
  '/analytics/facilities': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_ALL_ROLES,
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
  ],
  '/analytics/reports': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    ...REGION_ALL_ROLES,
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
  ],
  '/analytics': rolesForCapability('analytics.access'),

  // SETTINGS
  '/admin/system/agency-providers': ['SYSTEM_ADMIN', 'FINANCE_OFFICER'],
  '/admin/system/ai-usage': ['SYSTEM_ADMIN'],
  '/admin/system/employer-oncosts': ['SYSTEM_ADMIN', 'FINANCE_OFFICER'],
  '/admin/system/experience-lab':
    process.env.NODE_ENV === 'production' ? ['SYSTEM_ADMIN'] : ['SYSTEM_ADMIN', 'TENANT_ADMIN'],
  // W-H-A (2026-05-19) — class-sibling discovery: engine-versions admin page
  // (added by PR #85 "bundle-a Q12+Q5+G34") shipped without a route-access
  // mapping. Identical defect class to my own work; fixed alongside per the
  // statura-deep-fix class-discovery rule. Role gate matches the page's
  // RoleGuard (TENANT_CONFIGURATION_ALLOWED_ROLES).
  '/admin/system/engine-versions': TENANT_CONFIGURATION_ALLOWED_ROLES,
  '/admin/system/master-data': ['SYSTEM_ADMIN'],
  '/admin/system/workflows': ['SYSTEM_ADMIN'],
  '/admin/system/scim': TENANT_CONFIGURATION_ALLOWED_ROLES,
  '/admin/system/sso': TENANT_CONFIGURATION_ALLOWED_ROLES,
  '/admin/system/tenant-config': TENANT_CONFIGURATION_ALLOWED_ROLES,
  '/admin/system/signature-corrections': ['SYSTEM_ADMIN', 'TENANT_ADMIN'],
  '/admin/system/workforce-audit': ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'REGION_MANAGER'],
  '/admin/system/mobile-devices': ['SYSTEM_ADMIN', 'TENANT_ADMIN'],
  '/settings/modules': rolesForCapability('tenant.configure'),
  '/settings/portal': rolesForCapability('tenant.configure'),
  '/settings/assessments': rolesForCapability('tenant.configure'),
  '/settings/cert-allowance-registry': rolesForCapability('tenant.configure'),
  '/settings/cm-task-types': rolesForCapability('tenant.configure'),
  '/settings/document-templates': PEOPLE_SETTINGS_TOOL_READ_ROLES,
  // These leaf paths have their own RoleGuard cohorts. Without explicit entries
  // they fall through to /settings = SYSTEM/TENANT admin, so the shell hid the
  // nav and isRoleAllowedForPath blocked the roles the leaf would admit.
  // Services Australia settings leaf: lockstep with SETTINGS_SERVICES_AUSTRALIA_ROLES
  // (OWNER, SYSTEM_ADMIN, TENANT_ADMIN) — not SYSTEM_ADMIN-only (was blocking demo
  // Tenant Admins while the server layout already admitted them).
  '/settings/webhooks': ['SYSTEM_ADMIN'] as const,
  '/settings/integrations/services-australia': SETTINGS_SERVICES_AUSTRALIA_ROLES,
  '/settings/integrations/dhda': B2G_INTEGRATION_ROUTE_ROLES,
  '/settings/integrations/hi': ['OWNER', 'SYSTEM_ADMIN', 'TENANT_ADMIN'] as const,
  '/settings/integrations/xero': [
    'OWNER',
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FINANCE_OFFICER',
    'PAYROLL_OFFICER',
  ] as const,
  '/settings/integrations': [
    'OWNER',
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FINANCE_OFFICER',
    'PAYROLL_OFFICER',
  ] as const,
  '/settings/signature-templates': SIGNATURE_TEMPLATE_SETTINGS_ROLES,
  '/settings/funding-authorisations': ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FINANCE_OFFICER'] as const,
  '/settings/registration-providers': TENANT_CONFIGURATION_ALLOWED_ROLES,
  '/settings/billing': ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FINANCE_OFFICER'] as const,
  '/settings/feature-flags': ['SYSTEM_ADMIN'] as const,
  '/settings/escalations': TENANT_CONFIGURATION_ALLOWED_ROLES,
  '/settings/onboarding/client-packs': rolesForCapability('tenant.configure'),
  '/settings/onboarding/readiness': PEOPLE_SETTINGS_TOOL_READ_ROLES,
  '/settings/notification-routing/wizard': TENANT_CONFIGURATION_ALLOWED_ROLES,
  '/settings/webhooks/docs': ['SYSTEM_ADMIN'] as const,
  '/settings/onboarding': PEOPLE_SETTINGS_TOOL_READ_ROLES,
  '/settings/onboarding-packs': PEOPLE_SETTINGS_TOOL_READ_ROLES,
  // Statura system administrator control; the leaf layout also requires the platform
  // SYSTEM_ADMIN guard with platform.tenant.write. Never a provider role.
  '/settings/payroll/activation': ['SYSTEM_ADMIN'] as const,
  '/settings/payroll/aba-sender': ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FINANCE_OFFICER'] as const,
  '/settings/payroll/profile': SETTINGS_PAYROLL_PROVIDER_ROLES,
  '/settings/payroll/provider-responsibilities': SETTINGS_PAYROLL_PROVIDER_ROLES,
  '/settings/payroll/pay-roles': SETTINGS_PAY_ROLE_ROLES,
  '/settings/payroll/eba': rolesForCapability('tenant.configure'),
  // The pay-fact review queue leaf admits the checker cohort; see
  // PAY_EVENT_FACT_QUEUE_ROLES. The rest of the EBA workflow stays admin-only.
  '/settings/payroll/eba/*/manual-facts': PAY_EVENT_FACT_QUEUE_ROLES,
  '/settings/roster': rolesForCapability('tenant.configure'),
  '/settings/retention': ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'QUALITY_OFFICER'] as const,
  '/settings/security': rolesForCapability('tenant.configure'),
  '/settings/workflows/closeout': rolesForCapability('tenant.configure'),
  '/settings/workflows': rolesForCapability('tenant.configure'),
  '/settings/compliance': [...COMPLIANCE_SETTINGS_WRITE_ROLES, 'FACILITY_MANAGER'],
  // Personal notification choices, not tenant configuration. Longest-prefix
  // wins, so this must sit alongside the '/settings' entry rather than rely on
  // it. The command palette and the email footers both resolve through here.
  [OWN_NOTIFICATION_PREFERENCE_PATH]: SETTINGS_OWN_NOTIFICATION_ROLES,
  // Opening Settings does not admit unlisted configuration leaves.
  '/settings/*': TENANT_CONFIGURATION_ALLOWED_ROLES,
  '/settings': PEOPLE_SETTINGS_READ_ROLES,
};

export const ALWAYS_ALLOWED_EXACT = ['/overview', '/translate', '/forbidden'];
export const ALWAYS_ALLOWED_PREFIXES = ['/tasks', '/help', '/auth', '/access'];

function pathPrefixMatches(pathPrefix: string, pathname: string): boolean {
  if (pathPrefix.endsWith('/**')) {
    const parent = pathPrefix.slice(0, -3);
    return pathname.startsWith(parent + '/');
  }

  if (!pathPrefix.includes('*')) {
    return pathname === pathPrefix || pathname.startsWith(pathPrefix + '/');
  }

  const patternSegments = pathPrefix.split('/').filter(Boolean);
  const pathnameSegments = pathname.split('/').filter(Boolean);
  if (pathnameSegments.length < patternSegments.length) return false;

  return patternSegments.every(
    (segment, index) => segment === '*' || segment === pathnameSegments[index],
  );
}

/**
 * Resolve the authoritative workflow capability for a concrete path using the
 * same longest-prefix semantics as role access. Consumers must still evaluate
 * route role, module, scope and database policy independently.
 */
export function getWorkflowCapabilityForPath(pathname: string): WorkflowCapabilityId | undefined {
  let matchedCapability: WorkflowCapabilityId | undefined;
  let longestMatch = 0;
  for (const [pathPrefix, capability] of Object.entries(PATH_WORKFLOW_CAPABILITY_ACCESS)) {
    if (pathPrefixMatches(pathPrefix, pathname) && pathPrefix.length > longestMatch) {
      longestMatch = pathPrefix.length;
      matchedCapability = capability;
    }
  }
  return matchedCapability;
}

function getMatchedRoles(pathname: string): readonly string[] | undefined {
  let matchedRoles: readonly string[] | undefined;
  let longestMatch = 0;

  for (const [pathPrefix, roles] of Object.entries(PATH_ROLE_ACCESS)) {
    if (pathPrefixMatches(pathPrefix, pathname) && pathPrefix.length > longestMatch) {
      longestMatch = pathPrefix.length;
      matchedRoles = roles;
    }
  }

  return matchedRoles;
}

export function hasRouteRoleMapping(pathname: string): boolean {
  if (pathname === '/dashboards' || pathname.startsWith('/dashboards/')) {
    return hasDashboardRoleMapping(pathname);
  }

  if (
    ALWAYS_ALLOWED_EXACT.includes(pathname) ||
    ALWAYS_ALLOWED_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(prefix + '/'),
    )
  ) {
    return true;
  }

  return Boolean(getMatchedRoles(pathname));
}

export function isRoleAllowedForPath(
  pathname: string,
  role: string | null | undefined,
  billingPlan?: string | null,
  settingsDelegation?: SettingsDelegationPreference | null,
): boolean {
  // Role mappings apply to route paths; view state in query/hash is orthogonal.
  pathname = pathname.split(/[?#]/, 1)[0] ?? pathname;
  const normalizedRole = normalizeRole(role);
  if (!normalizedRole) return false;
  if (isSystemAdmin(normalizedRole)) return true;

  if (pathname === '/dashboards' || pathname.startsWith('/dashboards/')) {
    return isDashboardRoleAllowedForPath(pathname, normalizedRole);
  }

  if (
    ALWAYS_ALLOWED_EXACT.includes(pathname) ||
    ALWAYS_ALLOWED_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(prefix + '/'),
    )
  ) {
    return true;
  }

  if (
    pathname === '/people/policies-conduct/policies' ||
    pathname.startsWith('/people/policies-conduct/policies/')
  ) {
    if (normalizedRole === 'COMPLIANCE_OFFICER') return isComplianceOnlyPlan(billingPlan);
  }

  const matchedRoles = getMatchedRoles(pathname);
  if (!matchedRoles) return false;

  if (
    pathname === '/clients' ||
    pathname.startsWith('/clients/') ||
    pathname === '/compliance/ipc' ||
    pathname.startsWith('/compliance/ipc/')
  ) {
    if (normalizedRole === 'COMPLIANCE_OFFICER') {
      return (
        isComplianceOnlyPlan(billingPlan) &&
        matchedRoles.some((candidate) => normalizeRole(candidate) === normalizedRole)
      );
    }
  }
  return (
    matchedRoles.map((candidate) => normalizeRole(candidate)).includes(normalizedRole) ||
    isGrantedEndToEndSettingsRole(pathname, normalizedRole, settingsDelegation)
  );
}

export function canAccessRacClientHub(role: string | null | undefined): boolean {
  return (
    isRoleAllowedForPath('/residential/admissions', role) ||
    isRoleAllowedForPath('/residential/daily-living', role) ||
    isRoleAllowedForPath('/residential/care-minutes', role) ||
    isRoleAllowedForPath('/residential/rooms', role) ||
    isRoleAllowedForPath('/finance/respite-billing', role)
  );
}
